import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import ChevronDownIcon from "../../assets/svg/chevron-down-icon";
import CheckIcon from "../../assets/svg/check-icon";
import SearchInput from "./components/SearchInput";
import OptionsList from "./components/OptionsList";
import { SearchableDropdownOption, SearchableDropdownProps } from "./types";
import { visibleScrollbar } from "../../utils/functions/get-visible-scrollbar";
import { Tooltip } from "../Tooltip/Tooltip";
import InfoBtnIcon from "../../assets/svg/info-btn-icon";
import { WORKFLO_NAME } from "../../utils/constants/globalConstants";

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  headerText,
  options,
  value,
  showOptional = false,
  onChange,
  id,
  placeholder = "Select an option",
  disabled = false,
  className = "",
  triggerClassName = "",
  dropdownClassName = "",
  optionClassName = "",
  ariaLabel,
  ariaDescribedBy,
  showChevron = true,
  label,
  labelClassName = "",
  searchPlaceholder = "Search options...",
  showSearch = true,
  enableApiSearch = false,
  onSearch,
  initialOptions,
  searchDebounceMs = 500,
  loadingText = "",
  noResultsText = "No results found",
  sectionHeading = headerText || `All ${WORKFLO_NAME.plural}`,
  sectionHeadingClassName = "",
  showTooltip = false,
  tooltipText = "",
  triggerIconClassName = "",
  selectedOptionContainerClassName = "",
  placeholderClassName = "",
  error,
  errorMessage,
  errorClassName = "",
  Icon,
  iconClassName = "",
  dropdownMenuClassName,
  checkIconClassName = "",
  searchInputClassName = "",
  optionsListClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [apiOptions, setApiOptions] = useState<SearchableDropdownOption[]>([]);
  const [openUpward, setOpenUpward] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  const dropdownId =
    id || `searchable-dropdown-${Math.random().toString(36).slice(2, 9)}`;
  const listboxId = `${dropdownId}-listbox`;
  const triggerId = `${dropdownId}-trigger`;
  const defaultAriaDescribedBy = `${dropdownId}-description`;
  const errorId = `${dropdownId}-error`;

  const effectiveOptions = useMemo(() => {
    if (enableApiSearch) {
      if (searchQuery.trim()) {
        return apiOptions;
      } else {
        const selectedApiOption = apiOptions.find(
          (option) => option.value === value,
        );
        return [
          ...(initialOptions || []),
          ...(selectedApiOption ? [selectedApiOption] : []),
        ];
      }
    }
    return options;
  }, [
    enableApiSearch,
    searchQuery,
    apiOptions,
    initialOptions,
    options,
    value,
  ]);

  const selectedOption = useMemo(() => {
    const allOptions = enableApiSearch
      ? [...(options || []), ...(initialOptions || []), ...apiOptions]
      : options;

    return allOptions.find((option) => option.value === value) || null;
  }, [enableApiSearch, options, initialOptions, apiOptions, value]);

  const filteredOptions = useMemo(() => {
    let filtered;

    if (enableApiSearch) {
      filtered = effectiveOptions;
    } else {
      filtered = showSearch
        ? effectiveOptions.filter((option) => {
            const searchText = option?.label || option?.value;
            return searchText
              ?.toLowerCase()
              ?.includes(searchQuery?.toLowerCase());
          })
        : effectiveOptions;
    }

    return value
      ? filtered.filter((option) => option.value !== value)
      : filtered;
  }, [effectiveOptions, searchQuery, showSearch, enableApiSearch, value]);

  const handleApiSearch = useCallback(
    async (query: string) => {
      if (!enableApiSearch || !onSearch || !query.trim()) {
        setApiOptions([]);
        return;
      }

      setIsSearching(true);
      try {
        const results = await onSearch(query);
        setApiOptions(results);
      } catch (error) {
        console.error("API search failed:", error);
        setApiOptions([]);
      } finally {
        setIsSearching(false);
      }
    },
    [enableApiSearch, onSearch],
  );

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (enableApiSearch && searchQuery.trim()) {
      setIsTyping(true);
      searchTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        handleApiSearch(searchQuery);
      }, searchDebounceMs);
    } else if (enableApiSearch && !searchQuery.trim()) {
      setIsTyping(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, enableApiSearch, handleApiSearch, searchDebounceMs]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      setFocusedIndex(-1);
      setSearchQuery("");
      if (!isOpen && showSearch) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
    }
  }, [disabled, isOpen, showSearch]);

  const handleOptionSelect = useCallback(
    (option: SearchableDropdownOption) => {
      if (!option.disabled) {
        onChange(option.value, option);
        setIsOpen(false);
        setFocusedIndex(-1);
        setSearchQuery("");
        triggerRef.current?.focus();
      }
    },
    [onChange],
  );

  const handleClearSelection = useCallback(() => {
    onChange("", {} as SearchableDropdownOption);
    setFocusedIndex(-1);
  }, [onChange]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case "Enter":
        case " ":
          event.preventDefault();
          if (isOpen && focusedIndex >= 0) {
            handleOptionSelect(filteredOptions[focusedIndex]);
          } else {
            handleToggle();
          }
          break;
        case "Escape":
          if (isOpen) {
            setIsOpen(false);
            setFocusedIndex(-1);
            setSearchQuery("");
            triggerRef.current?.focus();
          }
          break;
        case "ArrowDown":
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(0);
          } else {
            setFocusedIndex((prev) => {
              const nextIndex =
                prev < filteredOptions.length - 1 ? prev + 1 : 0;
              for (let i = nextIndex; i < filteredOptions.length; i++) {
                if (!filteredOptions[i].disabled) return i;
              }
              for (let i = 0; i < nextIndex; i++) {
                if (!filteredOptions[i].disabled) return i;
              }
              return prev;
            });
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          if (isOpen) {
            setFocusedIndex((prev) => {
              const nextIndex =
                prev > 0 ? prev - 1 : filteredOptions.length - 1;
              for (let i = nextIndex; i >= 0; i--) {
                if (!filteredOptions[i].disabled) return i;
              }
              for (let i = filteredOptions.length - 1; i > nextIndex; i--) {
                if (!filteredOptions[i].disabled) return i;
              }
              return prev;
            });
          }
          break;
        case "Home":
          event.preventDefault();
          if (isOpen) {
            const firstEnabledIndex = filteredOptions.findIndex(
              (option) => !option.disabled,
            );
            if (firstEnabledIndex !== -1) {
              setFocusedIndex(firstEnabledIndex);
            }
          }
          break;
        case "End":
          event.preventDefault();
          if (isOpen) {
            for (let i = filteredOptions.length - 1; i >= 0; i--) {
              if (!filteredOptions[i].disabled) {
                setFocusedIndex(i);
                break;
              }
            }
          }
          break;
      }
    },
    [
      disabled,
      isOpen,
      focusedIndex,
      filteredOptions,
      handleToggle,
      handleOptionSelect,
    ],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
        setSearchQuery("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
      setSearchQuery("");
    }
  }, [isOpen]);

  // Decide open direction when the menu opens: below by default, flip above only
  // when it won't fit below the trigger and there's more room above. Plain effect
  // (like Dropdownv2) so it never blocks rendering.
  useEffect(() => {
    if (!isOpen) {
      setOpenUpward(false);
      return;
    }
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuHeight = dropdownRef.current?.offsetHeight ?? 250;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const gap = 4;
    setOpenUpward(spaceBelow < menuHeight + gap && spaceAbove > spaceBelow);
  }, [isOpen]);



  const renderTrigger = () => {
    return (
      <div
        className={`flex items-center gap-[8px] justify-between w-full px-[8px] py-[7px] ${selectedOptionContainerClassName}`}
      >
        {Icon && <Icon className={iconClassName} />}
        <span
          className={`text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1 min-w-0 pr-1 ${
            selectedOption
              ? "text-heading-text dark:text-heading-text-dk"
              : placeholderClassName ||
                "text-heading-text dark:text-heading-text-dk"
          }`}
        >
          {selectedOption
            ? selectedOption?.selectedOptionContent
              ? selectedOption?.selectedOptionContent
              : selectedOption.content
            : placeholder}
        </span>
        {showChevron && (
          <ChevronDownIcon
            className={`w-[8px] h-[8px] shrink-0 text-heading-text dark:text-heading-text-dk transition-transform duration-400 ${triggerIconClassName} ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label
          htmlFor={triggerId}
          className={`flex items-center gap-[2px] font-normal text-verba-field-label dark:text-verba-field-label-dk mb-1 ${labelClassName}`}
        >
          <p>{label}</p>
          {showOptional && (
            <span className="ml-0.5 text-xxs text-gray-placeholder dark:text-gray-placeholder-dk">
              {showOptional ? "(Optional)" : ""}
            </span>
          )}
          {showTooltip && (
            <Tooltip content={tooltipText}>
              <div>
                <InfoBtnIcon className="text-gray-placeholder dark:text-gray-placeholder-dk w-[18px] h-[18px]" />
              </div>
            </Tooltip>
          )}
        </label>
      )}

      <div
        ref={triggerRef}
        id={triggerId}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={ariaLabel || "Select an option"}
        aria-describedby={ariaDescribedBy || defaultAriaDescribedBy}
        tabIndex={disabled ? -1 : 0}
        className={`select-none cursor-pointer rounded-[6px] border border-border-gray dark:border-border-gray-dk bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] transition-colors focus:outline-none
          ${disabled && "opacity-80 !cursor-not-allowed"}
          ${triggerClassName}
        `}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        {renderTrigger()}
      </div>

      {error && errorMessage ? (
        <div
          id={errorId}
          role="alert"
          className={`mt-1 text-xxs text-verba-red-alert dark:text-verba-red-alert-dk ${errorClassName}`}
        >
          <span>{errorMessage}</span>
        </div>
      ) : null}

      {isOpen && (
        <div
          ref={dropdownRef}
          className={`absolute z-50 left-0 py-1 w-full backdrop-blur-[10px]
          bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk
          rounded-[8px] shadow-popper-blur-effect transition-all duration-200 ease-out ${
            openUpward ? "bottom-full mb-1" : "top-full mt-1"
          } ${dropdownClassName}`}
        >
          <SearchInput
            searchQuery={searchQuery}
            searchPlaceholder={searchPlaceholder}
            searchInputRef={searchInputRef}
            onSearchQueryChange={setSearchQuery}
            showSearch={showSearch}
            onKeyDown={handleKeyDown}
            className={searchInputClassName}
          />

          <div
            className={`max-h-[200px] overflow-y-auto ${visibleScrollbar(
              "[&::-webkit-scrollbar]:w-[8px]",
            )} ${dropdownMenuClassName}`}
          >
            {selectedOption && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearSelection();
                }}
                type="button"
                className="w-full border-b border-border-gray dark:border-border-gray-dk"
              >
                <div className="flex items-center justify-between bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk px-[14px] py-[10px]">
                  <div
                    className={`flex items-center gap-2 flex-1 min-w-0 text-md text-heading-text dark:text-heading-text-dk ${optionClassName}`}
                  >
                    {selectedOption.content}
                  </div>
                  <CheckIcon
                    className={`w-3 h-3 text-verba-blue dark:text-verba-blue-dk ${checkIconClassName}`}
                  />
                </div>
              </button>
            )}

            {sectionHeading && (
              <div
                className={`px-[12px] py-[6px] uppercase text-xxs font-semibold text-dark-gray dark:text-dark-gray-dk border-b border-border-gray dark:border-border-gray-dk bg-gray-50 dark:bg-gray-50-dk ${sectionHeadingClassName}`}
              >
                {sectionHeading}
              </div>
            )}

            <OptionsList
              filteredAndSortedOptions={filteredOptions}
              value={value}
              focusedIndex={focusedIndex}
              dropdownId={dropdownId}
              optionClassName={optionClassName}
              searchQuery={searchQuery}
              onOptionSelect={handleOptionSelect}
              onFocusedIndexChange={setFocusedIndex}
              isSearching={isTyping || isSearching}
              loadingText={loadingText}
              noResultsText={noResultsText}
              enableApiSearch={enableApiSearch}
              optionsListClassName={optionsListClassName}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
